<template>
    <DForm
        enctype="multipart/form-data"
        @submit.prevent
    >
        <input
            type="hidden"
            :value="form.id"
        >

        <!-- Copy brand -->
        <DFormRow
            v-if="form.id === undefined"
            :label="__('settings.brands.form.copy_brand_configuration')"
            :is-required="false"
            :hint="__('settings.brands.form.copy_brand_configuration_hint')"
        >
            <DSelect
                id="brand"
                v-model="selectedBrandId"
                :options="brandListOptions"
                :placeholder="__('settings.brands.form.copy_brand_placeholder')"
                @change="brandSelected()"
            />
        </DFormRow>

        <!-- Name -->
        <DFormRow
            :label="__('settings.brands.form.name')"
            :is-required="true"
            :hint="__('settings.brands.form.name_hint')"
        >
            <DInput
                id="name"
                v-model="form.name"
                name="name"
                maxlength="45"
                :is-valid="isFormFieldValid('name')"
                :invalid-feedback="errors.name"
            />
        </DFormRow>

        <!-- URL -->
        <DFormRow
            :label="__('settings.brands.form.url')"
            :is-required="true"
            :hint="__('settings.brands.form.url_hint')"
        >
            <DInput
                id="url"
                v-model="form.url"
                name="url"
                maxlength="100"
                :is-valid="isFormFieldValid('url')"
                :invalid-feedback="errors.url"
            />
        </DFormRow>

        <!-- Logo -->
        <DFormRow
            :label="__('settings.brands.form.logo')"
            :is-required="form.id === undefined"
            :hint="__('settings.brands.form.logo_hint')"
        >
            <div class="form-group position-relative">
                <DFileInput
                    id="logo"
                    accept="image/png"
                    :custom="true"
                    :multiple="false"
                    :is-valid="isFormFieldValid('logo')"
                    :invalid-feedback="errors.logo"
                    :placeholder="__('settings.brands.form.file_choose')"
                    @change="addFile"
                />
                <input
                    v-model="form.logo_base64"
                    type="hidden"
                    name="logo_base64"
                >
                <img
                    v-if="logoSrcContents !== null"
                    class="brand__image"
                    :alt="form.name"
                    :src="logoSrcContents"
                >
            </div>
        </DFormRow>

        <!-- Header Logos  -->
        <DFormRow
            :label="__('settings.tenant_settings.form.fields.header_logo')"
            :error="getDocumentHeaderLogoErrors()"
            :hint="__('settings.brands.form.header_logo_hint')"
        >
            <div v-if="form.document_header_logo_1 !== null">
                <img
                    class="logo-image"
                    alt="Header Logo 1"
                    :src="'data:image/svg+xml;base64,'+form.document_header_logo_1"
                >
                <DButton
                    id="document_header_logo_delete_1"
                    icon="trash-can-outline"
                    color="danger"
                    mini
                    @click="removeImage('document_header_logo_1')"
                />
            </div>
            <div v-if="form.document_header_logo_2 !== null">
                <img
                    class="logo-image"
                    alt="Header Logo 2"
                    :src="'data:image/svg+xml;base64,'+form.document_header_logo_2"
                >
                <DButton
                    id="document_header_logo_delete_2"
                    icon="trash-can-outline"
                    color="danger"
                    mini
                    @click="removeImage('document_header_logo_2')"
                />
            </div>
            <div v-if="form.document_header_logo_3 !== null">
                <img
                    class="logo-image"
                    alt="Header Logo 3"
                    :src="'data:image/svg+xml;base64,'+form.document_header_logo_3"
                >
                <DButton
                    id="document_header_logo_delete_3"
                    icon="trash-can-outline"
                    color="danger"
                    mini
                    @click="removeImage('document_header_logo_3')"
                />
            </div>
            <div v-if="form.document_header_logo_4 !== null">
                <img
                    class="logo-image"
                    alt="Header Logo 4"
                    :src="'data:image/svg+xml;base64,'+form.document_header_logo_4"
                >
                <DButton
                    id="document_header_logo_delete_4"
                    icon="trash-can-outline"
                    color="danger"
                    mini
                    @click="removeImage('document_header_logo_4')"
                />
            </div>
            <div v-if="form.document_header_logo_5 !== null">
                <img
                    class="logo-image"
                    alt="Header Logo 5"
                    :src="'data:image/svg+xml;base64,'+form.document_header_logo_5"
                >
                <DButton
                    id="document_header_logo_delete_5"
                    icon="trash-can-outline"
                    color="danger"
                    mini
                    @click="removeImage('document_header_logo_5')"
                />
            </div>
            <DFileInput
                id="document_header_logo"
                :custom="true"
                :multiple="true"
                :is-valid="isFormFieldValid('file')"
                @change="addHeaderFiles"
            />
        </DFormRow>

        <!-- Phone  -->
        <DFormRow
            :label="__('settings.brands.form.phone')"
            :is-required="true"
            :error="errors.phone"
            :hint="__('settings.brands.form.phone_hint')"
        >
            <DPhoneInput
                id="phone"
                v-model="form.phone"
                :phone-country-code-placeholder="phoneCountryCodePlaceholder"
                :is-valid="isFormFieldValid('phone')"
            />
        </DFormRow>

        <!-- Fax -->
        <DFormRow
            :label="__('settings.brands.form.fax')"
            :is-required="false"
            :error="errors.fax"
            :hint="__('settings.brands.form.fax_hint')"
        >
            <DPhoneInput
                id="fax"
                v-model="form.fax"
                :phone-country-code-placeholder="phoneCountryCodePlaceholder"
                :is-valid="isFormFieldValid('fax')"
            />
        </DFormRow>

        <!-- Emergency hotline phone -->
        <DFormRow
            :label="__('settings.brands.form.emergency_phone')"
            :is-required="false"
            :error="errors.emergency_phone"
            :hint="__('settings.brands.form.emergency_phone_hint')"
        >
            <DPhoneInput
                id="emergency_phone"
                v-model="form.emergency_phone"
                :phone-country-code-placeholder="phoneCountryCodePlaceholder"
                :is-valid="isFormFieldValid('emergency_phone')"
            />
        </DFormRow>

        <!-- Working hours -->
        <DFormRow
            :label="__('settings.brands.form.working_hours')"
            :is-required="true"
            :hint="__('settings.brands.form.working_hours_hint')"
        >
            <DInput
                id="working_hours"
                v-model="form.working_hours"
                name="working_hours"
                maxlength="45"
                :is-valid="isFormFieldValid('working_hours')"
                :invalid-feedback="errors.working_hours"
            />
        </DFormRow>

        <!-- Mail general -->
        <DFormRow
            :label="__('settings.brands.form.mailGeneral')"
            :is-required="true"
            :hint="__('settings.brands.form.mailGeneral_hint')"
        >
            <DInput
                id="mail_general"
                v-model="form.mail_general"
                name="mail_general"
                maxlength="45"
                :is-valid="isFormFieldValid('mail_general')"
                :invalid-feedback="errors.mail_general"
            />
        </DFormRow>

        <!-- Mail support -->
        <DFormRow
            :label="__('settings.brands.form.mailSupport')"
            :is-required="true"
            :hint="__('settings.brands.form.mailSupport_hint')"
        >
            <DInput
                id="mail_support"
                v-model="form.mail_support"
                name="mail_support"
                maxlength="45"
                :is-valid="isFormFieldValid('mail_support')"
                :invalid-feedback="errors.mail_support"
            />
        </DFormRow>

        <!-- Mail contract -->
        <DFormRow
            :label="__('settings.brands.form.mailContract')"
            :is-required="true"
            :hint="__('settings.brands.form.mailContract_hint')"
        >
            <DInput
                id="mail_contract"
                v-model="form.mail_contract"
                name="mail_contract"
                maxlength="45"
                :is-valid="isFormFieldValid('mail_contract')"
                :invalid-feedback="errors.mail_contract"
            />
        </DFormRow>

        <!-- Name general -->
        <DFormRow
            :label="__('settings.brands.form.nameGeneral')"
            :is-required="true"
            :hint="__('settings.brands.form.nameGeneral_hint')"
        >
            <DInput
                id="name_general"
                v-model="form.name_general"
                name="name_general"
                maxlength="45"
                :is-valid="isFormFieldValid('name_general')"
                :invalid-feedback="errors.name_general"
            />
        </DFormRow>

        <!-- Name support -->
        <DFormRow
            :label="__('settings.brands.form.nameSupport')"
            :is-required="true"
            :hint="__('settings.brands.form.nameSupport_hint')"
        >
            <DInput
                id="name_support"
                v-model="form.name_support"
                name="name_support"
                maxlength="45"
                :is-valid="isFormFieldValid('name_support')"
                :invalid-feedback="errors.name_support"
            />
        </DFormRow>

        <!-- Name contract -->
        <DFormRow
            :label="__('settings.brands.form.nameContract')"
            :is-required="true"
            :hint="__('settings.brands.form.nameContract_hint')"
        >
            <DInput
                id="name_contract"
                v-model="form.name_contract"
                name="name_contract"
                maxlength="45"
                :is-valid="isFormFieldValid('name_contract')"
                :invalid-feedback="errors.name_contract"
            />
        </DFormRow>

        <!-- Sender name general -->
        <DFormRow
            :label="__('settings.brands.form.senderNameGeneral')"
            :is-required="true"
            :hint="__('settings.brands.form.senderNameGeneral_hint')"
        >
            <DInput
                id="sender_name_general"
                v-model="form.sender_name_general"
                name="sender_name_general"
                maxlength="45"
                :is-valid="isFormFieldValid('sender_name_general')"
                :invalid-feedback="errors.sender_name_general"
            />
        </DFormRow>

        <!-- Sender name support -->
        <DFormRow
            :label="__('settings.brands.form.senderNameSupport')"
            :is-required="true"
            :hint="__('settings.brands.form.senderNameSupport_hint')"
        >
            <DInput
                id="sender_name_support"
                v-model="form.sender_name_support"
                name="sender_name_support"
                maxlength="45"
                :is-valid="isFormFieldValid('sender_name_support')"
                :invalid-feedback="errors.sender_name_support"
            />
        </DFormRow>

        <!-- Sender name contract -->
        <DFormRow
            :label="__('settings.brands.form.senderNameContract')"
            :is-required="true"
            :hint="__('settings.brands.form.senderNameContract_hint')"
        >
            <DInput
                id="sender_name_contract"
                v-model="form.sender_name_contract"
                name="sender_name_contract"
                maxlength="45"
                :is-valid="isFormFieldValid('sender_name_contract')"
                :invalid-feedback="errors.sender_name_contract"
            />
        </DFormRow>

        <!-- Primary color -->
        <DFormRow
            :label="__('settings.brands.form.primaryColor')"
            :is-required="true"
            :hint="__('settings.brands.form.primaryColor_hint')"
        >
            <DInput
                id="primary_color"
                v-model="form.primary_color"
                name="primary_color"
                maxlength="45"
                :is-valid="isFormFieldValid('primary_color')"
                :invalid-feedback="errors.primary_color"
            />
        </DFormRow>

        <!-- Secondary color -->
        <DFormRow
            :label="__('settings.brands.form.secondaryColor')"
            :is-required="true"
            :hint="__('settings.brands.form.secondaryColor_hint')"
        >
            <DInput
                id="secondary_color"
                v-model="form.secondary_color"
                name="secondary_color"
                maxlength="45"
                :is-valid="isFormFieldValid('secondary_color')"
                :invalid-feedback="errors.secondary_color"
            />
        </DFormRow>

        <!-- Status page URL -->
        <DFormRow
            :label="__('settings.brands.form.statusPageUrl')"
            :is-required="true"
            :hint="__('settings.brands.form.statusPageUrl_hint')"
        >
            <DInput
                id="status_page_url"
                v-model="form.status_page_url"
                name="status_page_url"
                maxlength="100"
                :is-valid="isFormFieldValid('status_page_url')"
                :invalid-feedback="errors.status_page_url"
            />
        </DFormRow>

        <!-- FAQ URL -->
        <DFormRow
            :label="__('settings.brands.form.faqUrl')"
            :is-required="true"
            :hint="__('settings.brands.form.faqUrl_hint')"
        >
            <DInput
                id="faq_url"
                v-model="form.faq_url"
                name="faq_url"
                maxlength="100"
                :is-valid="isFormFieldValid('faq_url')"
                :invalid-feedback="errors.faq_url"
            />
        </DFormRow>

        <!-- ZenDesk brand name -->
        <DFormRow
            :label="__('settings.brands.form.zendesk_brand_subdomain')"
            :is-required="true"
            :hint="__('settings.brands.form.zendesk_brand_subdomain_hint')"
        >
            <DInput
                id="zendesk_brand_subdomain"
                v-model="form.zendesk_brand_subdomain"
                name="name"
                maxlength="45"
                :is-valid="isFormFieldValid('zendesk_brand_subdomain')"
                :invalid-feedback="errors.zendesk_brand_subdomain"
            />
        </DFormRow>

        <!-- Contact URL -->
        <DFormRow
            :label="__('settings.brands.form.contactUrl')"
            :is-required="true"
            :hint="__('settings.brands.form.contactUrl_hint')"
        >
            <DInput
                v-model="form.contact_url"
                name="contact_url"
                maxlength="100"
                :is-valid="isFormFieldValid('contact_url')"
                :invalid-feedback="errors.contact_url"
            />
        </DFormRow>

        <!-- Contract Takeover Document URL-->
        <DFormRow
            :label="__('settings.brands.form.contractTakeoverDocumentUrl')"
            :is-required="false"
            :hint="__('settings.brands.form.contractTakeoverDocumentUrl_hint')"
        >
            <DInput
                id="contract_takeover_document_url"
                v-model="form.contract_takeover_document_url"
                name="contract_takeover_document_url"
                maxlength="255"
                :is-valid="isFormFieldValid('contract_takeover_document_url')"
                :invalid-feedback="errors.contract_takeover_document_url"
            />
        </DFormRow>

        <!-- Subcontractor List URL-->
        <DFormRow
            :label="__('settings.brands.form.subcontractorListUrl')"
            :is-required="false"
            :hint="__('settings.brands.form.subcontractorListUrl_hint')"
        >
            <DInput
                id="subcontractor_list_url"
                v-model="form.subcontractor_list_url"
                name="subcontractor_list_url"
                maxlength="255"
                :is-valid="isFormFieldValid('subcontractor_list_url')"
                :invalid-feedback="errors.subcontractor_list_url"
            />
        </DFormRow>

        <!-- Subcontractor List Version-->
        <DFormRow
            :label="__('settings.brands.form.subcontractorListVersion')"
            :is-required="false"
            :hint="__('settings.brands.form.subcontractorListVersion_hint')"
        >
            <DInput
                id="subcontractor_list_version"
                v-model="form.subcontractor_list_version"
                name="subcontractor_list_version"
                maxlength="255"
                :is-valid="isFormFieldValid('subcontractor_list_version')"
                :invalid-feedback="errors.subcontractor_list_version"
            />
        </DFormRow>

        <!-- Subcontractor List Version -->
        <DFormRow
            :label="__('settings.brands.form.subcontractor_list_version_date')"
            :is-required="false"
            :hint="__('settings.brands.form.subcontractor_list_version_date_hint')"
        >
            <DDateFormatInput
                id="subcontractor_list_version_date"
                v-model="form.subcontractor_list_version_date"
                format="YYYY-MM-DD"
                type="date"
                :placeholder="__('settings.brands.form.subcontractor_list_version_date')"
                :invalid-feedback="errors.subcontractor_list_version_date"
                :is-valid="isFormFieldValid('subcontractor_list_version_date')"
            />
        </DFormRow>

        <!-- TOM document URL -->
        <DFormRow
            :label="__('settings.brands.form.tom_document_url')"
            :is-required="false"
            :hint="__('settings.brands.form.tom_document_url_hint')"
        >
            <DInput
                id="tom_document_url"
                v-model="form.tom_document_url"
                name="tom_document_url"
                maxlength="256"
                :is-valid="isFormFieldValid('tom_document_url')"
                :invalid-feedback="errors.tom_document_url"
            />
        </DFormRow>

        <!-- TOM document version -->
        <DFormRow
            :label="__('settings.brands.form.tom_document_version')"
            :is-required="false"
            :hint="__('settings.brands.form.tom_document_version_hint')"
        >
            <DInput
                id="tom_document_version"
                v-model="form.tom_document_version"
                name="tom_document_url"
                maxlength="255"
                :is-valid="isFormFieldValid('tom_document_version')"
                :invalid-feedback="errors.tom_document_version"
            />
        </DFormRow>

        <!-- TOM document version date -->
        <DFormRow
            :label="__('settings.brands.form.tom_document_version_date')"
            :is-required="false"
            :hint="__('settings.brands.form.tom_document_version_date_hint')"
        >
            <DDateFormatInput
                id="tom_document_version_date"
                v-model="form.tom_document_version_date"
                format="YYYY-MM-DD"
                type="date"
                :placeholder="__('settings.brands.form.tom_document_version_date')"
                :invalid-feedback="errors.tom_document_version_date"
                :is-valid="isFormFieldValid('tom_document_version_date')"
            />
        </DFormRow>

        <DFormRow
            :label="__('settings.brands.form.default_customer_language')"
            :is-required="true"
            :error="errors.default_customer_language"
            :hint="__('settings.brands.form.default_customer_language_hint')"
        >
            <DSelect
                id="default_customer_language"
                :options="getAvailableLanguagesOption"
                :model-value="form.default_customer_language"
                @update:modelValue="updateDefaultLanguage"
            />
        </DFormRow>

        <DFormRow
            :label="__('settings.brands.form.additional_customer_languages')"
            :is-required="true"
            :error="errors.additional_customer_languages"
            :hint="__('settings.brands.form.additional_customer_languages_hint')"
        >
            <DMultiSelect
                id="additional_customer_languages"
                :key="additionalLanguagesKey"
                :options="getAdditionalCustomerLanguages(form.additional_customer_languages)"
                :selected="form.additional_customer_languages"
                :selection="true"
                selection-type="tags"
                :search="true"
                @update="(values) => updateAdditionalCustomerLanguages(values)"
            />
        </DFormRow>

        <!-- Winback email address -->
        <DFormRow
            :label="__('settings.brands.form.winback_email_address.label')"
            :is-required="false"
            :hint="__('settings.brands.form.winback_email_address.description')"
        >
            <DInput
                id="winback_email_address"
                v-model="form.winback_email_address"
                name="winback_email_address"
                maxlength="255"
                :is-valid="isFormFieldValid('winback_email_address')"
                :invalid-feedback="errors.winback_email_address"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.brands.form.phone_support')"
            :error="errors.phone_support"
            :hint="__('settings.brands.form.phone_support_hint')"
        >
            <DInput
                id="phone_support"
                v-model="form.phone_support"
                :is-valid="isFormFieldValid('phone_support')"
                maxlength="255"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.brands.form.phone_support_working_hours_translation_key')"
            :error="errors.phone_support_working_hours_translation_key"
            :hint="__('settings.brands.form.phone_support_working_hours_translation_key_hint')"
        >
            <DInput
                id="phone_support_working_hours_translation_key"
                v-model="form.phone_support_working_hours_translation_key"
                :is-valid="isFormFieldValid('phone_support_working_hours_translation_key')"
                maxlength="255"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.brands.form.mail_support_working_hours_translation_key')"
            :error="errors.mail_support_working_hours_translation_key"
            :hint="__('settings.brands.form.mail_support_working_hours_translation_key_hint')"
        >
            <DInput
                id="mail_support_working_hours_translation_key"
                v-model="form.mail_support_working_hours_translation_key"
                :is-valid="isFormFieldValid('mail_support_working_hours_translation_key')"
                maxlength="255"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.brands.form.chat_support_working_hours_translation_key')"
            :error="errors.chat_support_working_hours_translation_key"
            :hint="__('settings.brands.form.chat_support_working_hours_translation_key_hint')"
        >
            <DInput
                id="chat_support_working_hours_translation_key"
                v-model="form.chat_support_working_hours_translation_key"
                :is-valid="isFormFieldValid('chat_support_working_hours_translation_key')"
                maxlength="255"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.brands.form.customer_panel_url')"
            :is-required="true"
            :error="errors.customer_panel_url"
            :hint="__('settings.brands.form.customer_panel_url_hint')"
        >
            <DInput
                id="customer_panel_url"
                v-model="form.customer_panel_url"
                :placeholder="__('settings.brands.form.customer_panel_url')"
                :is-valid="isFormFieldValid('customer_panel_url')"
                maxlength="128"
            />
        </DFormRow>
        <hr>
        <div>
            <h2 class="h4">
                {{ __('settings.brands.form.invoice.header') }}
            </h2>
        </div>

        <!-- SEPA creditor_id -->
        <DFormRow
            :label="__('settings.brands.form.creditor_id')"
            :is-required="false"
            :hint="__('settings.brands.form.creditor_id_hint')"
        >
            <DInput
                id="creditor_id"
                v-model="form.creditor_id"
                name="creditor_id"
                maxlength="50"
                :is-valid="isFormFieldValid('creditor_id')"
                :invalid-feedback="errors.creditor_id"
            />
        </DFormRow>

        <!-- Disable invoicing -->
        <DFormRow
            :label="__('settings.brands.form.disable_invoicing')"
            :is-required="false"
            :error="errors.disable_invoicing"
            :hint="__('settings.brands.form.disable_invoicing_hint')"
        >
            <DSwitch
                v-model="form.disable_invoicing"
                color="success"
                variant="3d"
            />
        </DFormRow>

        <DFormRow
            :label="__('settings.brands.form.available_payments')"
            :is-required="true"
            :error="errors.available_payment_methods"
            :hint="__('settings.brands.form.available_payments_hint')"
        >
            <DMultiSelect
                id="payment_methods"
                :key="paymentMethodsKey"
                :options="getPaymentMethodOptions(form.available_payment_methods)"
                :selected="form.available_payment_methods"
                :selection="true"
                selection-type="tags"
                :search="true"
                @update="updatePayments"
            />
        </DFormRow>

        <DFormRow
            v-for="(upgradePaths, paymentMethod) in form.payment_methods_upgrade_paths"
            :key="paymentMethod"
            :label="paymentMethod"
            :is-required="false"
            :error="getErrorForUpgradePath(paymentMethod)"
            :hint="__('settings.brands.form.available_payments_method_hint')"
        >
            <DMultiSelect
                :id="`upgrades_${paymentMethod}`"
                :options="getUpgradePathOptions(paymentMethod, upgradePaths)"
                :selected="upgradePaths"
                :selection="true"
                selection-type="tags"
                :search="true"
                @update="(values) => updatePayment(paymentMethod, values)"
            />
        </DFormRow>

        <!-- Footer Logo  -->
        <DFormRow
            :label="__('settings.tenant_settings.form.fields.footer_logo')"
            :error="getDocumentFooterLogoErrors()"
            :hint="__('settings.brands.form.footer_logo_hint')"
        >
            <div v-if="form.document_footer_logo_1 !== null">
                <img
                    class="logo-image"
                    alt="Footer Logo 1"
                    :src="'data:image/png;base64,'+form.document_footer_logo_1"
                >
                <DButton
                    id="document_footer_logo_delete_1"
                    :disabled="form.document_footer_logo_2 !== null"
                    icon="trash-can-outline"
                    color="danger"
                    mini
                    @click="removeImage('document_footer_logo_1')"
                />
            </div>
            <div v-if="form.document_footer_logo_2 !== null">
                <img
                    class="logo-image"
                    alt="Footer Logo 2"
                    :src="'data:image/png;base64,'+form.document_footer_logo_2"
                >
                <DButton
                    id="document_footer_logo_delete_2"
                    icon="trash-can-outline"
                    color="danger"
                    mini
                    @click="removeImage('document_footer_logo_2')"
                />
            </div>
            <DFileInput
                v-else
                id="document_footer_logo"
                :custom="true"
                :multiple="true"
                :is-valid="isFormFieldValid('file')"
                @change="addFooterFile"
            />
        </DFormRow>

        <DFormRow
            :label="__('settings.brands.form.hide_phone_number_on_invoice')"
            :is-required="false"
            :error="errors.hide_phone_number_on_invoice"
            :hint="__('settings.brands.form.hide_phone_number_on_invoice_hint')"
        >
            <DSwitch
                v-model="form.hide_phone_number_on_invoice"
                color="success"
                variant="3d"
            />
        </DFormRow>

        <hr>
        <div>
            <h2 class="h4">
                {{ __('settings.brands.form.postal.header') }}
            </h2>
        </div>
        <DFormRow
            :label="__('settings.brands.form.postal.price.label')"
            :hint="__('settings.brands.form.postal.price.label_hint')"
        >
            <DInput
                id="postal_price"
                v-model="form.postal_price"
                name="postal_price"
                maxlength="13"
                :is-valid="isFormFieldValid('postal_price')"
                :invalid-feedback="errors.postal_price"
            />
        </DFormRow>
        <template
            v-if="showPostalOptions"
        >
            <DFormRow
                :label="__('settings.brands.form.postal.invoice_text.label')"
                :is-required="true"
                :hint="__('settings.brands.form.postal.invoice_text.description')"
            >
                <DInput
                    id="postal_invoice_text"
                    v-model="form.postal_invoice_text"
                    name="postal_invoice_text"
                    maxlength="255"
                    :is-valid="isFormFieldValid('postal_invoice_text')"
                    :invalid-feedback="errors.postal_invoice_text"
                />
                <TranslationLink
                    :tenant-languages="tenantLanguages"
                    :route="$route('settings.brands.translations', {brand: brand?.id ?? 0})"
                    :default-tenant-language="defaultTenantLanguage"
                />
            </DFormRow>
            <DFormRow
                :label="__('settings.brands.form.postal.accounting_product.label')"
                :is-required="true"
                :hint="__('settings.brands.form.postal.accounting_product.label_hint')"
            >
                <DSelect
                    id="postal_accounting_product_id"
                    v-model="form.postal_accounting_product_id"
                    :options="accountingProducts"
                    :placeholder="__('settings.brands.form.postal.accounting_product.label')"
                    :is-valid="isFormFieldValid('postal_accounting_product_id')"
                    :invalid-feedback="errors.postal_accounting_product_id"
                />
            </DFormRow>
            <DFormRow
                :label="__('settings.brands.form.postal.product.label')"
                :is-required="$page.props.tenantSettings?.finance_system === 'net_visor' ? true : false"
                :hint="__('settings.brands.form.postal.product.description')"
            >
                <DInput
                    id="postal_product_id"
                    v-model="form.postal_product_id"
                    name="postal_product_id"
                    :is-valid="isFormFieldValid('postal_product_id')"
                    :invalid-feedback="errors.postal_product_id"
                />
            </DFormRow>
        </template>

        <hr>
        <div>
            <h2 class="h4">
                {{ __('settings.brands.form.control_panel.header') }}
            </h2>
        </div>
        <DFormRow
            :label="__('settings.brands.form.control_panel.default_panel')"
            :is-required="true"
            :hint="__('settings.brands.form.control_panel.default_panel_hint')"
        >
            <DSelect
                id="default_panel"
                v-model="form.default_customer_panel"
                :options="controlPanels"
                :placeholder="__('settings.brands.form.control_panel.default_panel')"
                :is-valid="isFormFieldValid('default_panel')"
                :invalid-feedback="errors.default_panel"
            />
        </DFormRow>
        <!-- Zendesk widget key -->
        <DFormRow
            :label="__('settings.brands.form.control_panel.zendesk_widget_key')"
            :is-required="false"
            :hint="__('settings.brands.form.control_panel.zendesk_widget_key_hint')"
        >
            <DInput
                id="zendesk_widget_key"
                v-model="form.zendesk_widget_key"
                name="zendesk_widget_key"
                :placeholder="__('settings.brands.form.control_panel.zendesk_widget_key')"
                :is-valid="isFormFieldValid('zendesk_widget_key')"
                :invalid-feedback="errors.zendesk_widget_key"
            />
        </DFormRow>
        <!-- Additional configuration -->
        <DFormRow
            :label="__('settings.brands.form.control_panel.additional_configuration')"
            :is-required="true"
            :hint="__('settings.brands.form.control_panel.additional_configuration_hint')"
        >
            <DFormTextarea
                id="additional_configuration"
                v-model="form.additional_configuration"
                name="additional_configuration"
                :placeholder="__('settings.brands.form.control_panel.additional_configuration')"
                :is-valid="isFormFieldValid('additional_configuration')"
                :invalid-feedback="errors.additional_configuration"
                class="mb-0"
            />
        </DFormRow>
        <hr>
        <div>
            <h2 class="h4">
                {{ __('settings.brands.form.domains.header') }}
            </h2>
        </div>
        <DFormRow
            :label="__('settings.brands.form.domains.change_domain_owner_data_allowed_label')"
            :hint="__('settings.brands.form.domains.change_domain_owner_data_allowed_description')"
            :is-required="true"
        >
            <DSwitch
                id="change_domain_owner_data_allowed"
                v-model="form.change_domain_owner_data_allowed"
                color="success"
                variant="3d"
                :is-valid="isFormFieldValid('change_domain_owner_data_allowed')"
                :invalid-feedback="errors.change_domain_owner_data_allowed"
            />
        </DFormRow>
        <hr>
        <div>
            <h2 class="h4">
                {{ __('settings.brands.form.orders.header') }}
            </h2>
        </div>
        <DFormRow
            :label="__('settings.brands.form.orders.direct_marketing')"
            :hint="__('settings.brands.form.orders.direct_marketing_description')"
            :is-required="true"
        >
            <DSwitch
                id="direct_marketing"
                v-model="form.direct_marketing"
                color="success"
                variant="3d"
                :is-valid="isFormFieldValid('direct_marketing')"
                :invalid-feedback="errors.direct_marketing"
            />
        </DFormRow>
        <hr>
        <div>
            <h2 class="h4">
                {{ __('settings.brands.form.smtp_configuration.header') }}
            </h2>
        </div>
        <DFormRow
            :label="__('settings.brands.form.smtp_configuration.smtp_host')"
            :is-required="false"
            :hint="__('settings.brands.form.smtp_configuration.smtp_host_hint')"
        >
            <DInput
                id="smtp_host"
                v-model="form.smtp_host"
                name="smtp_host"
                :placeholder="__('settings.brands.form.smtp_configuration.smtp_host')"
                :is-valid="isFormFieldValid('smtp_host')"
                :invalid-feedback="errors.smtp_host"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.brands.form.smtp_configuration.smtp_port')"
            :is-required="false"
            :hint="__('settings.brands.form.smtp_configuration.smtp_port_hint')"
        >
            <DInput
                id="smtp_port"
                v-model="form.smtp_port"
                name="smtp_port"
                :placeholder="__('settings.brands.form.smtp_configuration.smtp_port')"
                :is-valid="isFormFieldValid('smtp_port')"
                :invalid-feedback="errors.smtp_port"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.brands.form.smtp_configuration.smtp_username')"
            :is-required="false"
            :hint="__('settings.brands.form.smtp_configuration.smtp_username_hint')"
        >
            <DInput
                id="smtp_username"
                v-model="form.smtp_username"
                name="smtp_username"
                :placeholder="__('settings.brands.form.smtp_configuration.smtp_username')"
                :is-valid="isFormFieldValid('smtp_username')"
                :invalid-feedback="errors.smtp_username"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.brands.form.smtp_configuration.smtp_password')"
            :is-required="false"
            :hint="__('settings.brands.form.smtp_configuration.smtp_password_hint')"
        >
            <DInput
                id="smtp_password"
                v-model="form.smtp_password"
                name="smtp_password"
                :placeholder="__('settings.brands.form.smtp_configuration.smtp_password')"
                :is-valid="isFormFieldValid('smtp_password')"
                :invalid-feedback="errors.smtp_password"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.brands.form.smtp_configuration.smtp_encryption')"
            :is-required="false"
            :hint="__('settings.brands.form.smtp_configuration.smtp_encryption_hint')"
        >
            <DInput
                id="smtp_encryption"
                v-model="form.smtp_encryption"
                name="smtp_encryption"
                :placeholder="__('settings.brands.form.smtp_configuration.smtp_encryption')"
                :is-valid="isFormFieldValid('smtp_encryption')"
                :invalid-feedback="errors.smtp_encryption"
            />
        </DFormRow>
        <DFormControls
            :cancel-route="$route('settings.brands.index')"
            :dirty="form.isDirty"
            @submit="submitForm"
        />
    </DForm>
</template>

<script>
import _ from 'lodash'
import { toRaw } from 'vue'
import TranslationLink from '../TranslationLink.vue'

export default {
    name: 'BrandForm',
    components: {
        TranslationLink
    },
    props: {
        errors: {
            type: Object,
            default: () => { return {} }
        },
        phoneCountryCodePlaceholder: {
            type: String,
            default: ''
        },
        brand: {
            type: Object,
            default: () => { return {} }
        },
        brandList: {
            type: Array,
            default: () => { return [] }
        },
        allPaymentMethods: {
            type: Array,
            default () {
                return []
            }
        },
        paymentMethods: {
            type: Array,
            default () {
                return []
            }
        },
        paymentMethodUpgradePaths: {
            type: Object,
            default () {
                return {}
            }
        },
        accountingProducts: {
            type: Array,
            default: () => { return [] }
        },
        availableLanguages: {
            type: Array,
            default: () => { return [] }
        },
        additionalCustomerLanguages: {
            type: Array,
            default: () => { return [] }
        },
        defaultCustomerLanguage: {
            type: String,
            default: null
        },
        controlPanels: {
            type: Array,
            default: () => { return [] }
        },
        defaultTenantLanguage: {
            type: Object,
            default: () => { return {} }
        },
        tenantLanguages: {
            type: Array,
            default: () => { return [] }
        }
    },
    emits: ['submit'],
    data () {
        return {
            form: this.$inertia.form({
                id: this.brand.id,
                name: this.brand.name,
                url: this.brand.url,
                logo: null,
                logo_base64: null,
                zendesk_brand_subdomain: this.brand.zendesk_brand_subdomain,
                phone: this.brand.phone,
                fax: this.brand.fax,
                emergency_phone: this.brand.emergency_phone,
                working_hours: this.brand.working_hours,
                mail_general: this.brand.mail_general,
                mail_support: this.brand.mail_support,
                mail_contract: this.brand.mail_contract,
                name_general: this.brand.name_general,
                name_support: this.brand.name_support,
                name_contract: this.brand.name_contract,
                sender_name_general: this.brand.sender_name_general,
                sender_name_support: this.brand.sender_name_support,
                sender_name_contract: this.brand.sender_name_contract,
                creditor_id: this.brand.creditor_id,
                primary_color: this.brand.primary_color,
                secondary_color: this.brand.secondary_color,
                status_page_url: this.brand.status_page_url,
                faq_url: this.brand.faq_url,
                contact_url: this.brand.contact_url,
                subcontractor_list_url: this.brand.subcontractor_list_url,
                subcontractor_list_version: this.brand.subcontractor_list_version,
                contract_takeover_document_url: this.brand.contract_takeover_document_url,
                subcontractor_list_version_date: this.brand.subcontractor_list_version_date,
                tom_document_url: this.brand.tom_document_url,
                tom_document_version: this.brand.tom_document_version,
                tom_document_version_date: this.brand.tom_document_version_date,
                disable_invoicing: this.brand.disable_invoicing ?? false,
                hide_phone_number_on_invoice: this.brand.hide_phone_number_on_invoice ?? false,
                available_payment_methods: this.paymentMethods,
                payment_methods_upgrade_paths: this.paymentMethodUpgradePaths,
                winback_email_address: this.brand.winback_email_address,
                customer_panel_url: this.brand.customer_panel_url,
                postal_price: this.brand.postal_price,
                postal_invoice_text: this.brand.postal_invoice_text,
                postal_accounting_product_id: this.brand.postal_accounting_product_id,
                postal_product_id: this.brand.postal_product_id,
                default_customer_language: this.brand.default_customer_language ?? this.defaultCustomerLanguage,
                additional_customer_languages: this.additionalCustomerLanguages,
                document_footer_logo_1: this.brand.document_footer_logo_1 ?? null,
                document_footer_logo_2: this.brand.document_footer_logo_2 ?? null,
                document_footer_logo: null,
                document_header_logo: null,
                document_header_logo_1: this.brand.document_header_logo_1 ?? null,
                document_header_logo_2: this.brand.document_header_logo_2 ?? null,
                document_header_logo_3: this.brand.document_header_logo_3 ?? null,
                document_header_logo_4: this.brand.document_header_logo_4 ?? null,
                document_header_logo_5: this.brand.document_header_logo_5 ?? null,
                default_customer_panel: this.brand.default_customer_panel ?? null,
                zendesk_widget_key: this.brand.zendesk_widget_key ?? null,
                additional_configuration: JSON.stringify(this.brand?.additional_configuration, null, 6) ?? null,
                change_domain_owner_data_allowed: this.brand.change_domain_owner_data_allowed ?? false,
                direct_marketing: this.brand.direct_marketing ?? false,
                phone_support: this.brand.phone_support ?? null,
                phone_support_working_hours_translation_key: this.brand.phone_support_working_hours_translation_key ?? null,
                mail_support_working_hours_translation_key: this.brand.mail_support_working_hours_translation_key ?? null,
                chat_support_working_hours_translation_key: this.brand.chat_support_working_hours_translation_key ?? null,
                smtp_host: this.brand.smtp_host ?? null,
                smtp_port: this.brand.smtp_port ?? null,
                smtp_username: this.brand.smtp_username ?? null,
                smtp_password: this.brand.smtp_password ?? null,
                smtp_encryption: this.brand.smtp_encryption ?? null
            }),
            logoSrcContents: this.brand.logo !== undefined ? `data:image/png;base64,${this.brand.logo}` : null,
            selectedBrandId: null,
            paymentMethodsKey: 0,
            additionalLanguages: [],
            additionalLanguagesKey: 0
        }
    },
    computed: {
        brandListOptions () {
            const list = this.brandList.map(brand => {
                return {
                    value: brand.id,
                    label: brand.name
                }
            })

            list.unshift({
                value: null,
                label: this.__('settings.brands.form.copy_brand_placeholder')
            })

            return list
        },
        paymentMethodOptions () {
            return this.allPaymentMethods.map((payment) => {
                return { value: payment, text: payment }
            })
        },
        showPostalOptions () {
            return parseFloat(this.form.postal_price) !== 0.0
        },
        upgradePathOptions () {
            const options = []

            for (const paymentMethod of this.form.available_payment_methods) {
                options.push({
                    value: paymentMethod,
                    text: paymentMethod
                })
            }

            return options
        },
        getAvailableLanguagesOption () {
            return this.availableLanguages
                .map((option) => ({ label: option.text, value: option.value }))
        }
    },
    mounted () {
        this.syncAdditionalLanguages()
    },
    methods: {
        submitForm () {
            this.$emit('submit', this.form)
        },
        addFile (fileList) {
            if (fileList.length === 0) {
                return
            }

            this.form.logo = fileList[0]
            this.logoSrcContents = URL.createObjectURL(fileList[0])
        },
        brandSelected () {
            if (this.selectedBrandId === null) {
                this.form.reset()
                this.logoSrcContents = null
                return
            }
            this.updateFormField()
        },
        updateAdditionalCustomerLanguages (values) {
            this.form.additional_customer_languages = values
        },
        async updatePayments (values) {
            const temporaryUpgradePaths = { ...this.form.payment_methods_upgrade_paths }
            this.form.payment_methods_upgrade_paths = {}

            const newPaymentMethodUpgradePaths = {}

            for (const paymentMethod of values) {
                newPaymentMethodUpgradePaths[paymentMethod] = (temporaryUpgradePaths[paymentMethod] ?? []).filter((value) => values.includes(value))

                await this.$nextTick()
            }

            this.form.available_payment_methods = values
            this.form.payment_methods_upgrade_paths = newPaymentMethodUpgradePaths
        },
        updatePayment (paymentMethod, values) {
            this.form.payment_methods_upgrade_paths[paymentMethod] = values
        },
        getErrorForUpgradePath (paymentMethod) {
            if (this.errors[`payment_methods_upgrade_paths.${paymentMethod}`] === undefined) {
                return undefined
            }

            return this.errors[`payment_methods_upgrade_paths.${paymentMethod}`].replace('payment_methods_upgrade_paths.', '')
        },
        updateFormField () {
            const selectedBrand = this.brandList.find((brand) => {
                return parseInt(brand.id) === parseInt(this.selectedBrandId)
            })

            this.form.name = ''
            this.form.url = selectedBrand.url
            this.form.logo = null
            this.logoSrcContents = selectedBrand.logo !== undefined ? `data:image/png;base64,${selectedBrand.logo}` : null
            if (this.logoSrcContents) {
                this.form.logo_base64 = selectedBrand.logo
            }
            this.form.zendesk_brand_subdomain = selectedBrand.zendesk_brand_subdomain
            this.form.phone = selectedBrand.phone
            this.form.fax = selectedBrand.fax
            this.form.emergency_phone = selectedBrand.emergency_phone
            this.form.working_hours = selectedBrand.working_hours
            this.form.mail_general = selectedBrand.mail_general
            this.form.mail_support = selectedBrand.mail_support
            this.form.mail_contract = selectedBrand.mail_contract
            this.form.name_general = selectedBrand.name_general
            this.form.name_support = selectedBrand.name_support
            this.form.name_contract = selectedBrand.name_contract
            this.form.sender_name_general = selectedBrand.sender_name_general
            this.form.sender_name_support = selectedBrand.sender_name_support
            this.form.sender_name_contract = selectedBrand.sender_name_contract
            this.form.creditor_id = selectedBrand.creditor_id
            this.form.primary_color = selectedBrand.primary_color
            this.form.secondary_color = selectedBrand.secondary_color
            this.form.status_page_url = selectedBrand.status_page_url
            this.form.faq_url = selectedBrand.faq_url
            this.form.contact_url = selectedBrand.contact_url
            this.form.subcontractor_list_url = selectedBrand.subcontractor_list_url
            this.form.subcontractor_list_version = selectedBrand.subcontractor_list_version
            this.form.contract_takeover_document_url = selectedBrand.contract_takeover_document_url
            this.form.subcontractor_list_version_date = selectedBrand.subcontractor_list_version_date
            this.form.tom_document_url = selectedBrand.tom_document_url
            this.form.tom_document_version = selectedBrand.tom_document_version
            this.form.tom_document_version_date = selectedBrand.tom_document_version_date
            this.form.disable_invoicing = selectedBrand.disable_invoicing
            this.form.hide_phone_number_on_invoice = selectedBrand.hide_phone_number_on_invoice
            this.form.available_payment_methods = selectedBrand.available_payment_methods
            this.form.payment_methods_upgrade_paths = selectedBrand.payment_methods_upgrade_paths
            this.form.winback_email_address = selectedBrand.winback_email_address
            this.form.customer_panel_url = selectedBrand.customer_panel_url
            this.form.postal_price = selectedBrand.postal_price
            this.form.postal_invoice_text = selectedBrand.postal_invoice_text
            this.form.postal_accounting_product_id = selectedBrand.postal_accounting_product_id
            this.form.default_customer_language = selectedBrand.default_customer_language
            this.form.additional_customer_languages = selectedBrand.additional_customer_languages ?? []
            this.form.additional_configuration = JSON.stringify(selectedBrand.additional_configuration)
            this.form.document_footer_logo_1 = selectedBrand.document_footer_logo_1
            this.form.document_footer_logo_2 = selectedBrand.document_footer_logo_2
            this.form.document_header_logo_1 = selectedBrand.document_header_logo_1
            this.form.document_header_logo_2 = selectedBrand.document_header_logo_2
            this.form.document_header_logo_3 = selectedBrand.document_header_logo_3
            this.form.document_header_logo_4 = selectedBrand.document_header_logo_4
            this.form.document_header_logo_5 = selectedBrand.document_header_logo_5

            ++this.paymentMethodsKey
        },
        getAdditionalCustomerLanguages (values) {
            const rawValues = toRaw(values)

            return _.cloneDeep(this.additionalLanguages)
                .map((option) => ({ ...option, selected: rawValues.includes(option.value) }))
        },
        getPaymentMethodOptions (values) {
            const rawValues = toRaw(values)

            return _.cloneDeep(this.paymentMethodOptions)
                .map((option) => ({ ...option, selected: rawValues.includes(option.value) }))
        },
        getUpgradePathOptions (paymentMethod, upgradePaths) {
            const rawValues = toRaw(upgradePaths)

            return _.cloneDeep(this.upgradePathOptions)
                .map((option) => ({ ...option, selected: rawValues.includes(option.value) }))
        },
        updateDefaultLanguage (value) {
            this.form.default_customer_language = value
            // Remove default language from additional customer languages
            this.form.additional_customer_languages = this.form.additional_customer_languages.filter(function (e) { return e !== value })
            this.syncAdditionalLanguages()
            ++this.additionalLanguagesKey
        },
        syncAdditionalLanguages () {
            this.additionalLanguages = []
            const rawValues = toRaw(this.additionalCustomerLanguages)
            this.availableLanguages
                .forEach((option) => {
                    if (option.value !== this.form.default_customer_language) {
                        this.additionalLanguages.push({ text: option.text, value: option.value, selected: rawValues ? rawValues.includes(option.value) : false })
                    }
                })
            return this.additionalLanguages
                .map((option) => ({ text: option.text, value: option.value, selected: rawValues ? rawValues.includes(option.value) : false }))
        },
        addFooterFile (fileList) {
            this.form.document_footer_logo = fileList
        },
        addHeaderFiles (fileList) {
            this.form.document_header_logo = fileList
        },
        removeImage (key) {
            this.form[key] = null
        },
        getDocumentHeaderLogoErrors () {
            const error = Object.keys(this.errors).find(key => key.startsWith('document_header_logo'))
            return error ? this.errors[error] : ''
        },
        getDocumentFooterLogoErrors () {
            const error = Object.keys(this.errors).find(key => key.startsWith('document_footer_logo'))
            return error ? this.errors[error] : ''
        }
    }
}
</script>

<style scoped>
.brand__image {
    max-width: 150px;
}
.logo-image {
    height: 100px;
    width: auto;
    margin-bottom: 5px;
    margin-top: 5px;
}
</style>
